<template>
  <div class="contacts-page">
    <StepProgress />
    
    <v-container class="py-8">
      <v-row justify="center">
        <v-col cols="12" md="8" lg="6">
          <v-card class="main-card" elevation="0">
            <v-card-title class="text-h4 text-center pa-6 primary--text">
              Contacts
            </v-card-title>
            
            <v-card-text class="pa-6">
              <div class="mb-6">
                <p class="text-h6 mb-4">
                  Who would you like to invite to contribute to {{ store.personName || 'this' }} memorial?
                </p>
                <p class="text-body-1 text--secondary mb-6">
                  Add email addresses of family and friends you'd like to invite to share their memories.
                </p>
              </div>

              <!-- Invite Preview -->
              <div class="d-flex justify-end mb-4">
                <v-btn
                  color="primary"
                  variant="outlined"
                  size="small"
                  prepend-icon="mdi-email-search-outline"
                  @click="openInvitePreview"
                >
                  Preview Invite
                </v-btn>
              </div>

              <!-- CSV Import Section -->
              <div class="mb-8">
                <h3 class="text-h5 mb-4 d-flex align-center">
                  <v-icon left color="primary">mdi-file-delimited-outline</v-icon>
                  Quick Import
                </h3>
                
                <div class="csv-import-section">
                  <v-textarea
                    v-model="csvInput"
                    label="Paste contacts (emails)"
                    placeholder="john@example.com, jane@example.com"
                    variant="outlined"
                    rows="3"
                    class="mb-3"
                    hint="Separate contacts with commas, line breaks, or spaces."
                    persistent-hint
                  ></v-textarea>
                  
                  <v-btn
                    color="primary"
                    @click="importFromCSV"
                    :disabled="!csvInput.trim()"
                    class="mb-4"
                  >
                    <v-icon left>mdi-import</v-icon>
                    Import Contacts
                  </v-btn>
                </div>
              </div>

              <v-divider class="my-6"></v-divider>

              <!-- Contacts Section -->
              <div class="mb-6">
                <h3 class="text-h5 mb-4 d-flex align-center">
                  <v-icon left color="primary">mdi-account-multiple-outline</v-icon>
                  Contacts
                </h3>
                
                <div class="contact-list">
                  <div
                    v-for="(contact, index) in contacts"
                    :key="`contact-${index}`"
                    class="contact-item mb-3"
                  >
                    <v-text-field
                      v-model="contacts[index]"
                      :label="getContactLabel(contact, index)"
                      variant="outlined"
                      density="comfortable"
                      :rules="contactRules"
                      class="contact-input"
                      :placeholder="getContactPlaceholder(contact)"
                    >
                      <template v-slot:prepend-inner>
                        <v-icon :color="getContactIconColor(contact)">
                          {{ getContactIcon(contact) }}
                        </v-icon>
                      </template>
                      <template v-slot:append>
                        <v-btn
                          v-if="contacts.length > 1"
                          icon
                          size="small"
                          color="error"
                          @click="removeContact(index)"
                        >
                          <v-icon>mdi-close</v-icon>
                        </v-btn>
                      </template>
                    </v-text-field>
                  </div>
                  
                  <v-btn
                    color="primary"
                    variant="outlined"
                    @click="addContact"
                    class="mb-4"
                  >
                    <v-icon left>mdi-plus</v-icon>
                    Add Another Contact
                  </v-btn>
                </div>
              </div>

              <!-- Contact Summary -->
              <v-expand-transition>
                <div v-if="hasValidContacts" class="contact-summary">
                  <v-alert
                    type="info"
                    variant="tonal"
                    class="mb-4"
                  >
                    <div class="d-flex align-center">
                      <div>
                        <strong>{{ validContactCount }} contacts will be invited</strong>
                        <br>
                        <span class="text-caption">
                          {{ emailCount }} emails
                        </span>
                        <br>
                        <span class="text-caption">
                          They will receive an invitation to contribute photos and stories to this memory.
                        </span>
                      </div>
                    </div>
                  </v-alert>
                </div>
              </v-expand-transition>
            </v-card-text>

            <v-card-actions class="pa-6 pt-0">
              <v-btn
                variant="text"
                @click="previousStep"
                class="mr-4"
              >
                <v-icon left>mdi-arrow-left</v-icon>
                Back
              </v-btn>

              <v-spacer></v-spacer>

              <!-- Save button - saves and proceeds to Share page on success -->
              <v-btn
                color="primary"
                size="large"
                rounded="0"
                elevation="0"
                :disabled="saving"
                :loading="saving"
                @click="saveAndProceed"
                class="px-8"
              >
                Save
                <v-icon right class="ml-2">mdi-content-save</v-icon>
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- CSV Import Confirmation Dialog -->
    <v-dialog v-model="showImportDialog" max-width="90vw" width="auto" persistent>
      <v-card class="import-dialog">
        <v-card-title class="text-h5 pa-6">
          Import Contacts?
        </v-card-title>
        
        <v-card-text class="pa-6 pt-0">
          <p class="mb-3">
            You have text in the Quick Import field that hasn't been imported yet.
          </p>
          <p class="mb-0">
            Would you like to import these contacts before proceeding?
          </p>
        </v-card-text>
        
        <v-card-actions class="pa-6 pt-0 dialog-actions">
          <v-btn
            color="grey"
            variant="text"
            @click="proceedWithoutImport"
            class="dialog-btn"
          >
            No, ignore that information
          </v-btn>
          <v-btn
            color="primary"
            @click="importAndProceed"
            class="dialog-btn"
          >
            Yes, use those contacts
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Invite Preview Dialog -->
    <v-dialog v-model="showInvitePreview" max-width="700" scrollable>
      <v-card>
        <v-card-title class="d-flex align-center justify-space-between pa-4">
          <span>Email Invite Preview</span>
          <v-btn icon variant="text" @click="showInvitePreview = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pa-0" style="height: 600px;">
          <div v-if="invitePreviewLoading" class="d-flex align-center justify-center" style="height: 100%;">
            <v-progress-circular indeterminate color="primary"></v-progress-circular>
          </div>
          <div v-else-if="invitePreviewError" class="d-flex align-center justify-center pa-6" style="height: 100%;">
            <v-alert type="error" variant="tonal">{{ invitePreviewError }}</v-alert>
          </div>
          <iframe
            v-else
            :srcdoc="invitePreviewHtml"
            class="invite-preview-frame"
            sandbox="allow-same-origin"
          ></iframe>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useMemorialStore } from '../stores/memorial'
import StepProgress from '../components/StepProgress.vue'
import apiService from '../services/api'
import { API_BASE_URL } from '@/config/branding'

const router = useRouter()
const store = useMemorialStore()

// Reactive data
const contacts = ref([''])
const csvInput = ref('')
const showImportDialog = ref(false)
const saving = ref(false)
const showInvitePreview = ref(false)
const invitePreviewHtml = ref('')
const invitePreviewLoading = ref(false)
const invitePreviewError = ref('')

// Validation rules
const contactRules = [
  v => !v || isValidEmail(v) || 'Must be a valid email'
]

// Helper functions
const isValidEmail = (value) => {
  return /.+@.+\..+/.test(value)
}

const isValidPhone = (value) => {
  return /^[+]?[\s\-()]*([0-9][\s\-()]*){7,}/.test(value)
}

const getContactType = (contact) => {
  if (!contact || !contact.trim()) return 'unknown'
  if (isValidEmail(contact.trim())) return 'email'
  if (isValidPhone(contact.trim())) return 'phone'
  return 'unknown'
}

const getContactIcon = (contact) => {
  const type = getContactType(contact)
  switch (type) {
    case 'email': return 'mdi-email-outline'
    case 'phone': return 'mdi-phone-outline'
    default: return 'mdi-account-outline'
  }
}

const getContactIconColor = (contact) => {
  const type = getContactType(contact)
  return type !== 'unknown' ? 'primary' : 'grey'
}

const getContactLabel = (contact, index) => {
  const type = getContactType(contact)
  if (type === 'email') return `Email ${index + 1}`
  if (type === 'phone') return `Phone ${index + 1}`
  return `Contact ${index + 1}`
}

const getContactPlaceholder = (contact) => {
  const type = getContactType(contact)
  if (type === 'email') return 'email@example.com'
  if (type === 'phone') return '+1 (555) 123-4567'
  return 'Email or phone number'
}

// Computed properties
const validContacts = computed(() => {
  return contacts.value.filter(contact => 
    contact && contact.trim() && (isValidEmail(contact.trim()) || isValidPhone(contact.trim()))
  )
})

const emailCount = computed(() => {
  return validContacts.value.filter(contact => isValidEmail(contact.trim())).length
})


const validContactCount = computed(() => {
  return validContacts.value.length
})

const hasValidContacts = computed(() => {
  return validContactCount.value > 0
})

// Methods
const addContact = () => {
  contacts.value.push('')
}

const removeContact = (index) => {
  if (contacts.value.length > 1) {
    contacts.value.splice(index, 1)
  }
}

const importFromCSV = () => {
  if (!csvInput.value.trim()) return
  
  // Split by various delimiters and clean up
  const rawContacts = csvInput.value
    .split(/[,\n\r\t;]+/)
    .map(contact => contact.trim())
    .filter(contact => contact.length > 0)
  
  // Add valid contacts to the list
  const newContacts = []
  rawContacts.forEach(contact => {
    if (isValidEmail(contact) || isValidPhone(contact)) {
      // Check if contact already exists
      if (!contacts.value.includes(contact)) {
        newContacts.push(contact)
      }
    }
  })
  
  // Remove empty contacts and add new ones
  contacts.value = contacts.value.filter(c => c.trim() !== '')
  contacts.value.push(...newContacts)
  
  // Always ensure at least one empty field
  if (contacts.value.length === 0 || contacts.value[contacts.value.length - 1].trim() !== '') {
    contacts.value.push('')
  }
  
  // Clear CSV input
  csvInput.value = ''
}

// Navigation
const saveAndProceed = async () => {
  if (saving.value) return

  // Check if there's unimported CSV data
  if (csvInput.value.trim()) {
    // Check if there are any valid contacts in the CSV input
    const rawContacts = csvInput.value
      .split(/[,\n\r\t;]+/)
      .map(contact => contact.trim())
      .filter(contact => contact.length > 0)

    const hasValidCSVContacts = rawContacts.some(contact =>
      isValidEmail(contact) || isValidPhone(contact)
    )

    if (hasValidCSVContacts) {
      showImportDialog.value = true
      return
    }
  }

  // Save contacts to store before saving memorial
  store.updateContacts(validContacts.value)

  saving.value = true

  try {
    // Build memorial data from store
    const memorialData = {
      uuid: store.memorialUuid,
      userUuid: store.user?.uuid || null,
      adminUuids: null,
      nameOfPerson: store.personName || null,
      memorialDate: store.memorialDate || null,
      address: store.memorialAddress || null,
      details: store.memorialDetails || null,
      mediaUuids: null,
      stories: buildStoriesArray(),
      contacts: buildContactsArray(),
      hostMessage: store.personalMessage || null,
      hostVideoMessageUuid: null,
      stripeCustomerId: null
    }


    // Use create or edit based on whether memorial UUID exists
    const response = store.memorialUuid
      ? await apiService.editMemorial(memorialData, store.apiToken)
      : await apiService.createMemorial(memorialData, store.apiToken)

    if (!response.success) {
      throw new Error(response.msg || 'Failed to save memory')
    }

    console.log('Memorial saved successfully:', response)

    // Store the memorial UUID if this was a new memorial
    if (response.memorial?.uuid && !store.memorialUuid) {
      store.setMemorialUuid(response.memorial.uuid)
      if (response.memorial.shareSlug) {
        store.setShareSlug(response.memorial.shareSlug)
      }
    }

    // Update change tracking after successful save
    store.markChangesSaved()

    // Navigate to Share page on successful save
    store.goToStep(8) // Share step
    router.push('/share')

  } catch (error) {
    console.error('Memorial save error:', error)
    // On error, stay on current page (do not navigate)
    // TODO: Show error message to user
  } finally {
    saving.value = false
  }
}

const importAndProceed = async () => {
  importFromCSV()
  showImportDialog.value = false
  // After importing, proceed with save
  await saveAndProceed()
}

const proceedWithoutImport = async () => {
  showImportDialog.value = false
  // Proceed with save without importing CSV data
  await saveAndProceed()
}

const previousStep = () => {
  store.goToStep(6) // Payment step
  router.push('/payment')
}

const buildStoriesArray = () => {
  return store.stories
    .filter(story => story && story.trim())
    .map(story => ({
      uuid: null,
      text: story.trim()
    }))
}

const buildContactsArray = () => {
  return store.contacts
    .filter(contact => contact && contact.trim())
    .map(contact => {
      const contactData = {
        uuid: null,
        email: null,
        phone: null,
        firstName: null,
        surname: null
      }
      
      // Check if it's an email or phone
      if (/.+@.+\..+/.test(contact.trim())) {
        contactData.email = contact.trim()
      } else if (/^[+]?[\s\-()]*([0-9][\s\-()]*){7,}/.test(contact.trim())) {
        contactData.phone = contact.trim()
      }
      
      return contactData
    })
    .filter(contact => contact.email || contact.phone) // Only include valid contacts
}

// Watch for changes and update store
watch(contacts, (newContacts) => {
  const valid = newContacts.filter(contact => 
    contact && contact.trim() && (isValidEmail(contact.trim()) || isValidPhone(contact.trim()))
  )
  store.updateContacts(valid)
}, { deep: true })

const openInvitePreview = async () => {
  showInvitePreview.value = true
  invitePreviewLoading.value = true
  invitePreviewError.value = ''
  invitePreviewHtml.value = ''
  try {
    const baseUrl = process.env.NODE_ENV === 'production'
      ? API_BASE_URL
      : 'http://localhost:8088'
    const response = await fetch(
      `${baseUrl}/memorial/invite-preview/${store.memorialUuid}`,
      { headers: { pid: store.apiToken } }
    )
    if (!response.ok) throw new Error(`HTTP ${response.status}`)
    invitePreviewHtml.value = await response.text()
  } catch (error) {
    console.error('Error loading invite preview:', error)
    invitePreviewError.value = 'Failed to load invite preview.'
  } finally {
    invitePreviewLoading.value = false
  }
}

// Initialize from store
onMounted(async () => {
  // If user has a memorial UUID, reload from server to ensure fresh data
  if (store.memorialUuid && store.isAccountCreated && store.isEmailVerified) {
    console.log('Reloading memorial data from server...')
    const loaded = await store.loadUserMemorialFromServer()
    if (loaded) {
      console.log('Memorial data reloaded successfully')
    }
  }

  // Load existing contacts from store
  if (store.contacts && store.contacts.length > 0) {
    contacts.value = [...store.contacts]
    // Ensure at least one empty field
    if (contacts.value[contacts.value.length - 1].trim() !== '') {
      contacts.value.push('')
    }
  }

  store.goToStep(7) // Set current step to Contacts

  // Initialize page tracking for logged-in users
  if (store.isAccountCreated && store.isEmailVerified && store.apiToken) {
    store.enterPage('contacts')
  }
})
</script>

<style lang="scss" scoped>
@use "~@/styles/variables" as *;
@use "~@/styles/mixins" as *;
.contacts-page {
  @include page-container;
}

.main-card {
  @include main-card;
  
  .v-card-title {
    @include section-header;
  }
}

.contact-list {
  background: $gray-50;
  border-radius: $border-radius-lg;
  padding: 1.5rem;
}

.contact-item {
  background: $white;
  border-radius: $border-radius;
  padding: 0.5rem;
  border: $border-width-thin solid $border;
  transition: all $transition-duration $transition-timing;
  
  &:hover {
    border-color: $primary;
    box-shadow: 0 2px 8px rgba($primary, 0.1);
  }
}

.contact-input {
  margin: 0;
}

.csv-import-section {
  background: $gray-50;
  border-radius: $border-radius-lg;
  padding: 1.5rem;
  border: $border-width-thin solid $border;
}


.import-dialog {
  .dialog-actions {
    display: flex;
    gap: 12px;
    justify-content: flex-end;
    
    .dialog-btn {
      flex: 0 1 auto;
      white-space: nowrap;
    }
  }
}

@include mobile {
  .pa-6 {
    padding: 1rem !important;
  }
  
  .contact-list {
    padding: 1rem;
  }
  
  .import-dialog {
    .dialog-actions {
      flex-direction: column;
      gap: 8px;
      
      .dialog-btn {
        width: 100%;
        flex: none;
      }
    }
  }
}

.invite-preview-frame {
  width: 100%;
  height: 100%;
  border: none;
  display: block;
}
</style>